<script setup lang="ts">
import { createReusableTemplate } from '@vueuse/core'
import {
    GIT_PROPS,
    hasGitProps,
    MAX_TOOL_SLOTS,
    mustGateEntryLabel,
    toolEntryNames,
    toolSlotKeyProblem,
} from '@zerohuman/shared'
import {
    Button,
    Card,
    CardContent,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Separator,
    Textarea,
    ZhAddTile,
    ZhConfirmButton,
    ZhDetailsFold,
    ZhEmptyState,
    ZhFieldError,
    ZhFormField,
    ZhFormLayout,
    ZhPanel,
    ZhSavePreview,
    ZhSectionHeading,
    ZhStatusPill,
} from '@zerohuman/ui'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
    api,
    type BindingKind,
    type CredentialScheme,
    type Member,
    type Role,
    type SavedSignInRow,
    type TaskRow,
    type Team,
    type ToolBindingRow,
    type ToolLayer,
} from '@/api'
import ExpressToolDirectory from '@/components/ExpressToolDirectory.vue'
import McpPermissionsPicker from '@/components/McpPermissionsPicker.vue'
import OAuthAuthorizeParams from '@/components/OAuthAuthorizeParams.vue'
import { oauthServerName, oauthStartRefusalMessage } from '@/components/oauth-start-refusals'
import ToolImageInput from '@/components/ToolImageInput.vue'
import { bindRefusalMessage, revokeRefusalMessage } from '@/components/tool-binding-refusals'
import { useFieldValidation } from '@/composables/useFieldValidation'
import { useFormDraft } from '@/composables/useFormDraft'
import { useUiMode } from '@/composables/useUiMode'
import { slotActionsFromText, slotActionsText } from '@/lib/expressTemplate'
import { buildToolPreview } from '@/lib/formPreview'
import { memberLabel } from '@/lib/memberLabel'
import { authorizeParamsFromText } from '@/lib/oauthAuthorizeParams'
import { personLabel } from '@/lib/presentation'
import { toolCards } from '@/lib/toolCards'

const props = defineProps<{
    lockLayer?: ToolLayer
    lockLayerId?: string
    /**
     * #1762: the host decides how the bindings open. Given a sentence, that sentence leads and the explanation of how
     * binding works sits behind a closed "How this works" fold (the Tools page). Without one, the explanation is the
     * lead paragraph, as every other screen that embeds the bindings has always shown it.
     */
    intro?: string
}>()

/** How binding works, written once: the lead paragraph by default, the fold's content under a host's own intro. */
const [DefineHowBindingWorks, HowBindingWorks] = createReusableTemplate()

/** #1749: Standard names no error code and no member ID; Advanced shows both, as the page always did. */
const { mode: uiMode } = useUiMode()
const advancedMode = computed(() => uiMode.value === 'advanced')

const rows = ref<ToolBindingRow[]>([])
const teams = ref<Team[]>([])
const members = ref<Member[]>([])
const roles = ref<Role[]>([])
const tasks = ref<TaskRow[]>([])
const enterpriseId = ref('')
const error = ref<string | null>(null)
const busy = ref(false)
const editingId = ref<string | null>(null)
const editingExpress = computed(() => Boolean(rows.value.find(row => row.id === editingId.value)?.expressToolId))
const token = ref('')
const mcpUrl = ref('')
const toolType = ref('')
const displayName = ref('')
const imageUrl = ref('')
const layer = ref<ToolLayer>(props.lockLayer ?? 'enterprise')
const layerId = ref(props.lockLayerId ?? '')
const authMode = ref<'bearer' | 'oauth'>('bearer')
const clientId = ref('')
const clientSecret = ref('')
const scopes = ref('')
const authorizeParams = ref('')
/**
 * Issue #673: a binding is either MCP or Computer-Use. The kind decides which half of the form is
 * asked for — an MCP server and its auth, or the sites a browser may open and where it starts — and
 * Save sends only that kind's values, so switching kind on an edit replaces the binding rather than
 * leaving the other kind's fields behind.
 *
 * #1406 adds a third: a credential the web tools add to requests to the hosts it names, in the header and the way
 * it says. Its secret is typed here and never shown back: an edit leaves it as saved unless it is typed again. It
 * is kept apart from the MCP token and out of the form draft, so it is never stored in the browser.
 */
const kind = ref<BindingKind>('mcp')
const sites = ref('')
const startUrl = ref('')
const hosts = ref('')
const credentialHeader = ref('authorization')
const credentialScheme = ref<CredentialScheme>('bearer')
const credentialSecret = ref('')
/**
 * Where a key (token) MCP binding's key is sent: the request header (empty means `authorization`) and whether it goes
 * after the word Bearer or on its own. Whatever the person names; the page names no vendor's header. An OAuth sign-in
 * has neither, since its access token always goes in Authorization.
 */
const keyHeader = ref('')
const keyScheme = ref<CredentialScheme>('bearer')
/**
 * The sample web addresses on the Start URL and MCP server URL fields. They live here, not as plain attributes in the
 * template, because vue-tsc 3.3.12 turns a `//` inside a static attribute into the start of a comment in the code it
 * type-checks. Inside a `ZhFormField` slot that cuts off the slot's `controlId` and `helpId`, so typecheck fails.
 */
const START_URL_EXAMPLE = 'https://crm.example.com/login'
const MCP_URL_EXAMPLE = 'https://mcp.example.com'
const URL_PLACEHOLDER = 'https://…'
/**
 * Custom props: key → value settings any tool may read beyond its server and token, typed behind the Custom props
 * button, which stays shut until pressed. Each tool's docs page lists its keys; a code host's `git.*` ones are what a
 * workspace clones and pushes with (#1025). The form names no key and no vendor itself.
 */
const propsOpen = ref(false)
const propRows = ref<Array<{ key: string; value: string }>>([])
/**
 * What the API takes. An edit always sends the whole map, so removing a row removes that prop. A create sends it
 * only when one is typed: creating over an existing binding re-binds it in place, and an untouched form must leave
 * that binding's saved props alone rather than wipe them.
 */
const propsPayload = computed(() =>
    Object.fromEntries(
        propRows.value
            .map(prop => [prop.key.trim(), prop.value.trim()] as const)
            .filter(([key, value]) => key !== '' && value !== ''),
    ),
)
const propCount = computed(() => Object.keys(propsPayload.value).length)

function toggleProps(): void {
    propsOpen.value = !propsOpen.value
    if (propsOpen.value && propRows.value.length === 0) {
        propRows.value.push({ key: '', value: '' })
    }
}

/**
 * The tool slots this binding fills (`codehost`): a task that names its tools by one of these keys uses this binding
 * for it. Typed as keys separated by commas or spaces, behind the Fills slots button. Whoever may edit the binding may
 * set them, an Express one included. An edit always sends the whole list, so emptying it clears them; a create sends
 * them only when some are typed, so re-binding in place leaves the saved ones alone, as props do.
 */
const slotsOpen = ref(false)
const slotsText = ref('')
/** The typed keys, each once, in the order typed. */
function slotKeys(text: string): string[] {
    return [...new Set(text.split(/[\s,]+/).filter(Boolean))]
}
const slotList = computed(() => slotKeys(slotsText.value))

function toggleSlots(): void {
    slotsOpen.value = !slotsOpen.value
}

/** Why the typed slots cannot be saved, by the shared rule the API applies, or null when they can. */
function slotsIssue(text: string): { message: string; fix: string } | null {
    const keys = slotKeys(text)
    if (keys.length > MAX_TOOL_SLOTS) {
        return { message: `More than ${MAX_TOOL_SLOTS} slots.`, fix: `List at most ${MAX_TOOL_SLOTS}.` }
    }
    const reserved = keys.find(key => toolSlotKeyProblem(key) === 'tool_slot_key_reserved')
    if (reserved) {
        return { message: `"${reserved}" is the OS's own and cannot be a slot.`, fix: 'Use a key of your own.' }
    }
    const invalid = keys.find(key => toolSlotKeyProblem(key) !== null)
    if (invalid) {
        return {
            message: `"${invalid}" is not a slot key.`,
            fix: 'Start with a lowercase letter, then up to 31 lowercase letters, digits, - or _.',
        }
    }
    return null
}

/**
 * Its action map (#956): for each action a task names under a slot this connection fills (`codehost.pr.merge`), the
 * tool it is on this server, typed one `action=tool` a line as the Staff page types a directory entry's. Behind the
 * Action map button, beside Fills slots. An edit always sends the whole map, so a removed line clears that action; a
 * create sends it only when some are typed, so a new connection otherwise starts from its directory entry's map.
 */
const actionsOpen = ref(false)
const actionsText = ref('')
/** The typed map, or an empty one while a line cannot be read: the field's own rule says which. */
const actionMap = computed(() => {
    try {
        return slotActionsFromText(actionsText.value)
    } catch {
        return {}
    }
})
const actionCount = computed(() => Object.keys(actionMap.value).length)

function toggleActions(): void {
    actionsOpen.value = !actionsOpen.value
}

/** Why the typed action map cannot be saved, by the shape the API checks, or null when it can. */
function slotActionsIssue(text: string): { message: string; fix: string } | null {
    try {
        slotActionsFromText(text)
        return null
    } catch {
        return {
            message: 'Some lines are not action=tool pairs, or name an action twice.',
            fix: 'Use one action=tool a line, like pr.merge=merge_pull_request, or tool#field=value to pin one use of a tool.',
        }
    }
}

/** How many custom props a bound row carries, for its line on the card. */
function propsOn(row: ToolBindingRow): number {
    return Object.keys(row.props ?? {}).length
}

/**
 * The calls this binding says a task must gate, as a person reads them (`tool (method=merge)` for an entry narrowed
 * to some calls). Shown, never edited here: the list is the CEO's, changed through the API, and no save on this page
 * sends it, so every entry stays as stored.
 */
function mustGateOn(row: ToolBindingRow): string {
    return (row.mustGate ?? []).map(mustGateEntryLabel).join(', ')
}

/** How many actions a bound row maps, for its line on the card. */
function actionsOn(row: ToolBindingRow): number {
    return Object.keys(row.slotActions ?? {}).length
}

/**
 * The actions "Add missing actions" writes onto `row`, or null when it offers none: the ones its directory entry maps
 * and it does not (the API's `missingSlotActions`), on a connection that fills a slot, since only a slot's tasks name
 * actions. A run of such a task parks as `tool_action_unmapped` until the map names them.
 */
function missingActionsOf(row: ToolBindingRow): Record<string, string> | null {
    const missing = row.missingSlotActions ?? {}
    return (row.slots?.length ?? 0) > 0 && Object.keys(missing).length > 0 ? missing : null
}

/**
 * One save, no form, as "Add git settings" is: the map as it is plus the missing actions, and nothing else. An action
 * the connection already maps keeps its own tool, never the directory entry's. Then the bindings reload, which clears
 * the notice, and a run parked on one of the actions resumes by itself.
 */
async function addMissingActions(row: ToolBindingRow, missing: Record<string, string>): Promise<void> {
    busy.value = true
    error.value = null
    try {
        await api.patchTool(row.id, { slotActions: { ...missing, ...(row.slotActions ?? {}) } })
        await loadBindings()
    } catch (caught) {
        error.value =
            bindRefusalMessage(caught) ??
            'Could not add the missing actions. Edit the binding and add them under Action map.'
    } finally {
        busy.value = false
    }
}

/** Whether a binding carries the `git.*` props a workspace reads, counted as the API's `gitBindingFields` counts them. */
function hasGit(row: ToolBindingRow): boolean {
    return hasGitProps(row.props)
}

/**
 * Active tasks that work in a workspace. While no binding carries `git.*` props, every run of theirs fails
 * `workspace_repo_no_clone_address`, as every one did after #1025 shipped (2026-09-30). Only the whole page can
 * tell: a page locked to one layer sees that layer's bindings alone.
 */
const workspaceTasks = computed(() =>
    tasks.value.filter(
        task =>
            task.status !== 'deprecated' &&
            toolEntryNames(task.currentVersion?.tools ?? []).some(tool => tool.startsWith('workspace.')),
    ),
)
const gitMissing = computed(() => !props.lockLayer && workspaceTasks.value.length > 0 && !rows.value.some(hasGit))

/**
 * The binding of the same tool that has the `git.*` props `row` lacks. A run reads the `git.*` props of the nearest
 * binding of each tool only, so one that reaches `row` first clones with none, whatever the other holds.
 */
function gitGapFrom(row: ToolBindingRow): ToolBindingRow | null {
    if (hasGit(row)) {
        return null
    }
    return rows.value.find(other => other.toolType === row.toolType && other.id !== row.id && hasGit(other)) ?? null
}

/** The `git.*` props of a map, and nothing else: the only props a workspace reads, and the only ones the fix writes. */
function gitPropsOf(map: Record<string, string> | undefined): Record<string, string> {
    return Object.fromEntries(
        Object.entries(map ?? {}).filter(([key, value]) => key.startsWith('git.') && value !== ''),
    )
}

/**
 * Whether two bindings are on the same MCP server, compared as the API's `bindingPropsPreset` compares them: no
 * surrounding space, no case, no trailing slash. A binding with no server (a browser or a host credential) is on none.
 */
function sameServer(left: string | undefined, right: string | undefined): boolean {
    const bare = (url: string | undefined) => (url ?? '').trim().toLowerCase().replace(/\/+$/, '')
    return bare(left) !== '' && bare(left) === bare(right)
}

/**
 * The binding whose `git.*` props "Add git settings" may copy onto `row`: one of the same tool, on the same server,
 * that has them. Its `git.host` and clone address are where `row`'s token is sent, so another server's would send it
 * to the wrong host (a GitHub Enterprise Server binding beside a github.com one, or the reverse). Where the gap line
 * names a binding on this server, it is that one.
 */
function gitSourceFor(row: ToolBindingRow): ToolBindingRow | null {
    return (
        rows.value.find(
            other =>
                other.toolType === row.toolType &&
                other.id !== row.id &&
                hasGit(other) &&
                sameServer(other.mcpUrl, row.mcpUrl),
        ) ?? null
    )
}

/**
 * What "Add git settings" writes onto `row` (#1752), or null when the row shows no warning: no active task works in a
 * workspace, the binding already has its `git.*` props, or the OS has no values to offer it.
 *
 * The values come from a binding of the same tool on the same server, every `git.*` key of it (`git.repo` too: the
 * enterprise chose it there), else from the preset the API puts on the row. The page names no host of its own, and a
 * preset never sets `git.repo`: which repository a layer works in is never the vendor's to say. Only the binding's own
 * row is read, so a page locked to one layer, which sees no sibling, still fixes from the preset.
 */
function gitFillFor(row: ToolBindingRow): Record<string, string> | null {
    if (workspaceTasks.value.length === 0 || hasGit(row)) {
        return null
    }
    const sibling = gitSourceFor(row)
    const fill = sibling ? gitPropsOf(sibling.props) : gitPropsOf(row.propsPreset)
    if (!sibling) {
        delete fill[GIT_PROPS.repo]
    }
    return Object.keys(fill).length > 0 ? fill : null
}

/** Each listed binding's fill values, by binding id, for the rows that warn: worked out once per reload. */
const gitFills = computed(() => {
    const fills: Record<string, Record<string, string>> = {}
    for (const row of rows.value) {
        const fill = gitFillFor(row)
        if (fill) {
            fills[row.id] = fill
        }
    }
    return fills
})

/**
 * One save, no form (#1752): the binding's props as they are plus the fill values, and nothing else, so no token,
 * credential or other field goes with it. A prop already saved is kept as it is, never overwritten or removed. Then
 * the bindings reload, which is what clears the warning. The page is busy while it saves, so the button cannot be
 * pressed twice. A refusal says why in words and leaves the warning up.
 */
async function addGitSettings(row: ToolBindingRow, fill: Record<string, string>): Promise<void> {
    busy.value = true
    error.value = null
    try {
        await api.patchTool(row.id, { props: { ...fill, ...(row.props ?? {}) } })
        await loadBindings()
    } catch (caught) {
        error.value =
            bindRefusalMessage(caught) ??
            'Could not add the git settings. Edit the binding and add them under Custom props.'
    } finally {
        busy.value = false
    }
}

const isComputerUse = computed(() => kind.value === 'computer-use')
const isWebCredential = computed(() => kind.value === 'web-credential')
/** A stored secret: a value for the repository commands a task version names, with custom props and nothing else. */
const isSecret = computed(() => kind.value === 'secret')
const isMcp = computed(() => kind.value === 'mcp')

/** A textarea as the API wants it: one entry per line, blanks dropped. */
function lines(text: string): string[] {
    return text
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
}
const siteList = computed(() => lines(sites.value))
const hostList = computed(() => lines(hosts.value))
/**
 * The saved computer-use sign-ins each Computer-Use binding holds, keyed by binding id (#676).
 *
 * A sign-in a person completed during a run is reused by later runs, so the same binding that grants the
 * sites is where the member, their lead, or the CEO comes to take it back — revoking here makes the
 * following run meet the login wall again. Only member and site are ever listed; the replayable session
 * stays in the API.
 */
const savedSignIns = ref<Record<string, SavedSignInRow[]>>({})
const redirectUri = ref('')
const adding = ref(false)
/**
 * Whether an edit is also re-scoping the binding.
 *
 * Moving a binding between layers changes who can use the credential, so editing hides the layer
 * selects until they are asked for. Rotating a token stays a one-field edit, and a re-scope is a
 * deliberate act rather than something a stale select does on the way past.
 */
const scopeOpen = ref(false)
const saving = computed(() => Boolean(editingId.value))
const showForm = computed(() => adding.value || saving.value)
/**
 * The rules a save of the chosen kind already needs (the refusals below say them), checked while typing (#1757):
 * every binding needs its tool type (the field's `required`); a Computer-Use binding needs a site; a credential needs
 * a host, a header name and, when new, its secret; a new MCP binding on a bearer token needs the token; a new one on
 * OAuth needs its server URL (Connect is refused without it) and extra sign-in parameters that are unique
 * name=value lines (`authorizeParamsFromText`'s rule). Only the chosen kind's fields are checked.
 */
const fields = useFieldValidation(
    computed(() => ({
        toolType: toolType.value,
        mcpUrl: mcpUrl.value,
        authorizeParams: authorizeParams.value,
        sites: sites.value,
        hosts: hosts.value,
        credentialHeader: credentialHeader.value,
        credentialSecret: credentialSecret.value,
        token: token.value,
        slots: slotsText.value,
        slotActions: actionsText.value,
    })),
    {
        toolType: value =>
            value.trim() === ''
                ? { message: 'The tool type is empty.', fix: 'Name the tool prefix, like github, linear or slack.' }
                : null,
        mcpUrl: value =>
            isMcp.value && authMode.value === 'oauth' && !saving.value && value.trim() === ''
                ? {
                      message: 'The MCP server URL is empty.',
                      fix: 'Paste the address of the MCP server, like https://mcp.example.com.',
                  }
                : null,
        authorizeParams: value => {
            if (!isMcp.value || authMode.value !== 'oauth' || editingExpress.value) return null
            try {
                authorizeParamsFromText(value)
                return null
            } catch {
                return {
                    message: 'Some lines are not unique name=value pairs.',
                    fix: 'Use unique name=value lines, one per line, like prompt=consent, or leave it empty.',
                }
            }
        },
        sites: value =>
            isComputerUse.value && lines(value).length === 0
                ? {
                      message: 'No site is listed.',
                      fix: 'Add at least one site, one host per line, like crm.example.com.',
                  }
                : null,
        hosts: value =>
            isWebCredential.value && lines(value).length === 0
                ? { message: 'No host is listed.', fix: 'Add at least one host, one per line, like api.example.com.' }
                : null,
        credentialHeader: value =>
            isWebCredential.value && value.trim() === ''
                ? { message: 'The header name is empty.', fix: 'Name the header it is sent in, like authorization.' }
                : null,
        credentialSecret: value =>
            (isWebCredential.value || isSecret.value) && !saving.value && value.trim() === ''
                ? { message: 'The secret is empty.', fix: 'Paste the secret this credential sends.' }
                : null,
        token: value =>
            isMcp.value && authMode.value === 'bearer' && !saving.value && value === ''
                ? { message: 'The token is empty.', fix: 'Paste the API key, PAT or bearer token for this server.' }
                : null,
        slots: slotsIssue,
        slotActions: slotActionsIssue,
    },
    // The parameters keep the id their own component and its specs already use.
    { idPrefix: 'tool-binding', ids: { authorizeParams: 'oauth-authorize-params' } },
)
const route = useRoute()
const draft = useFormDraft(
    props.lockLayer && props.lockLayerId ? `tool-bindings:${props.lockLayer}:${props.lockLayerId}` : 'tool-bindings',
    {
        toolType,
        displayName,
        imageUrl,
        mcpUrl,
        token,
        layer,
        layerId,
        authMode,
        clientId,
        clientSecret,
        scopes,
        authorizeParams,
    },
    { enabled: () => !editingId.value },
)
if (props.lockLayer) {
    layer.value = props.lockLayer
}
if (props.lockLayerId) {
    layerId.value = props.lockLayerId
}

const layerOptions: Array<{ value: ToolLayer; label: string }> = [
    { value: 'enterprise', label: 'Enterprise (CEO / company-wide)' },
    { value: 'team', label: 'Team (shared)' },
    { value: 'member', label: 'Member (that agent’s own tools)' },
    { value: 'role', label: 'Role' },
    { value: 'task', label: 'Task' },
]

const layerTargets = computed(() => {
    if (layer.value === 'enterprise') {
        return [{ id: enterpriseId.value, label: 'Enterprise' }]
    }
    if (layer.value === 'team') {
        return teams.value.map(team => ({ id: team.id, label: team.name }))
    }
    if (layer.value === 'member') {
        return members.value.map(member => ({
            id: member.id,
            // An unnamed COO has no address yet: it reads "COO", not "COO · " (#1815).
            label: [memberLabel(member), member.email].filter(Boolean).join(' · '),
        }))
    }
    if (layer.value === 'role') {
        return roles.value.map(role => ({ id: role.id, label: role.name }))
    }
    return tasks.value.map(task => ({ id: task.id, label: task.slug }))
})

/** The teams and members an Express tool can be connected for, as the directory's own chooser lists them. */
const expressTeams = computed(() => teams.value.map(team => ({ id: team.id, label: team.name })))
const expressMembers = computed(() => members.value.map(member => ({ id: member.id, label: memberLabel(member) })))

/** Where a new binding applies, in words for the save preview: a name, never an id, slug or email. */
function appliesToWords(): string {
    if (layer.value === 'enterprise') return 'the whole company'
    if (layer.value === 'team') {
        const team = teams.value.find(row => row.id === layerId.value)
        return team ? `the team ${team.name}` : 'one team'
    }
    if (layer.value === 'member') {
        const member = members.value.find(row => row.id === layerId.value)
        return member ? memberLabel(member) : 'one member'
    }
    if (layer.value === 'role') {
        const role = roles.value.find(row => row.id === layerId.value)
        return role ? `the role ${role.name}` : 'one role'
    }
    const task = tasks.value.find(row => row.id === layerId.value)
    return task?.name ? `the task ${task.name}` : 'one task'
}

/** The form's primary button, and so the save preview's confirm. */
const primaryLabel = computed(() => {
    if (isComputerUse.value) return saving.value ? 'Save' : 'Bind browser'
    if (isWebCredential.value) return saving.value ? 'Save' : 'Attach credential'
    if (isSecret.value) return saving.value ? 'Save' : 'Store secret'
    if (authMode.value === 'bearer') return saving.value ? 'Save' : 'Bind MCP'
    return saving.value ? 'Reconnect / change permissions' : 'Connect with OAuth'
})

/**
 * The add form's save preview (#1756): binding a tool, or starting its OAuth sign-in, first says in plain words what
 * will be connected and where, and nothing is sent until that is confirmed. Never the token, URL or tool type. Any
 * change to the form hides it as stale. Editing a bound tool keeps saving at once, as it always has.
 */
const preview = ref<string[] | null>(null)

/** How the tool will connect, as the preview says it. */
function connectsBy(): 'browser' | 'sign-in' | 'token' {
    if (isComputerUse.value) return 'browser'
    if (isMcp.value && authMode.value === 'oauth') return 'sign-in'
    return 'token'
}

const AUTHORIZE_PARAMS_INVALID =
    'Use unique name=value lines for additional sign-in parameters. Leave client credentials, redirect, state, PKCE and permissions to their dedicated fields.'

/**
 * Opens the preview, unless the sign-in parameters are malformed: those are refused here, with the message starting
 * the sign-in gave, so the preview only ever describes a connection that can start.
 */
function openPreview(): void {
    // Validate first (CTO decision 19 on #1756): a field that breaks its rule shows its error, takes focus and opens
    // no preview. Confirm checks again in `bind()` or `startOAuth()`.
    if (fields.check() !== null) return
    error.value = null
    if (isMcp.value && authMode.value === 'oauth') {
        try {
            authorizeParamsFromText(authorizeParams.value)
        } catch {
            error.value = AUTHORIZE_PARAMS_INVALID
            return
        }
    }
    preview.value = buildToolPreview({
        displayName: displayName.value,
        connects: connectsBy(),
        appliesTo: appliesToWords(),
    })
}

function submitForm(): void {
    if (saving.value) {
        void bind()
        return
    }
    openPreview()
}

async function confirmPreview(): Promise<void> {
    preview.value = null
    if (isMcp.value && authMode.value === 'oauth') await startOAuth()
    else await bind()
}

watch(
    [
        layer,
        layerId,
        kind,
        toolType,
        displayName,
        imageUrl,
        mcpUrl,
        authMode,
        token,
        clientId,
        clientSecret,
        scopes,
        authorizeParams,
        sites,
        startUrl,
        hosts,
        credentialHeader,
        credentialScheme,
        credentialSecret,
        keyHeader,
        keyScheme,
        propRows,
        slotsText,
        actionsText,
        showForm,
    ],
    () => {
        preview.value = null
    },
    { deep: true },
)

/** What the binding being edited currently applies to, for the line above "Change where this applies". */
const layerLabel = computed(() => {
    if (layer.value === 'enterprise') {
        return 'the whole enterprise'
    }
    const target = layerTargets.value.find(option => option.id === layerId.value)
    return target ? `${layer.value} ${target.label}` : layer.value
})

/**
 * The card list is driven only by actual bindings: a vendor nobody bound (including the identity login this session
 * signed in with) gets no card, so there is no hardcoded vendor catalog to keep in sync. A vendor bound at several
 * layers keeps one row per layer inside its single card, and so does every prefix that reaches one MCP server
 * (`toolCards`): several accounts of one vendor read as one group.
 */
const cards = computed(() => toolCards(rows.value))

onMounted(load)
watch(
    () => [props.lockLayer, props.lockLayerId],
    () => {
        if (props.lockLayer) {
            layer.value = props.lockLayer
        }
        if (props.lockLayerId) {
            layerId.value = props.lockLayerId
        }
        void loadBindings()
    },
)

watch(layer, () => {
    // Editing keeps the binding's own layer and target: `edit()` sets both, and the row's layerId
    // must survive into Save / OAuth reconnect. A layer the user picks goes through `pickLayer`.
    if (props.lockLayerId || editingId.value) {
        return
    }
    layerId.value = layerTargets.value[0]?.id ?? ''
})

/**
 * Only a layer the *user* chose moves the target with it.
 *
 * The watcher above cannot tell that apart from `edit()` adopting a row's own layer, so re-scoping
 * sets the layer here and then reads the fresh targets, leaving an adopted layer untouched.
 */
function pickLayer(next: ToolLayer): void {
    layer.value = next
    if (props.lockLayerId) {
        return
    }
    layerId.value = layerTargets.value[0]?.id ?? ''
}

async function load(): Promise<void> {
    try {
        const me = await api.me()
        enterpriseId.value = me?.enterprise.id ?? ''
        teams.value = await api.teams()
        members.value = await api.members()
        roles.value = await api.roles()
        tasks.value = (await api.tasks({ limit: 200 })).data
        if (props.lockLayerId) {
            layerId.value = props.lockLayerId
        } else if (layer.value === 'enterprise') {
            layerId.value = enterpriseId.value
        } else if (!layerId.value) {
            layerId.value = layerTargets.value[0]?.id ?? ''
        }
        await loadBindings()
        if (typeof route.query.reconnect === 'string') {
            const connection = rows.value.find(row => row.id === route.query.reconnect)
            if (connection) edit(connection)
        }
        await openConnectFromQuery()
        try {
            redirectUri.value = (await api.oauthRedirectUri()).redirectUri
        } catch {
            redirectUri.value = ''
        }
        if (route.query.oauth === 'ok') {
            error.value = null
            resetCreateForm()
            if (route.query.catalog === 'empty')
                error.value =
                    'Connected, but the server returned no actions. Edit the connection to choose permissions, or check the server’s tool settings.'
            if (route.query.catalog === 'unavailable')
                error.value =
                    'Authorization was saved, but actions could not be loaded. Check the connection and retry actions in the task picker.'
        }
        if (route.query.oauth_error) {
            error.value =
                'OAuth did not complete. For an Express tool, press Connect and sign in again. For a tool you set up yourself, check the MCP URL, client id, and that this redirect URI is registered on the app.'
        }
    } catch {
        error.value = 'Could not load tools.'
    }
}

/**
 * Setup's Connect button (#1739) opens this page with the tool type a team still needs (`?connect=slack`): the same
 * add form anyone opens with Add Tool, its tool type filled in. A connection being edited is left alone.
 *
 * #637: the missing-tools list and the Needs you notice open it for one member (`?connect=gitea&member=<id>`): the form
 * then opens on that member's own layer, with them chosen under "Bind on", so the page says who it is for and what is
 * saved reaches them. A member the page does not list, or a page held to a layer of its own, keeps the form's layer.
 */
async function openConnectFromQuery(): Promise<void> {
    const wanted = route.query.connect
    if (typeof wanted !== 'string' || wanted === '' || editingId.value) {
        return
    }
    toolType.value = wanted
    adding.value = true
    const forMember = route.query.member
    if (props.lockLayer || typeof forMember !== 'string' || !members.value.some(member => member.id === forMember)) {
        return
    }
    layer.value = 'member'
    // The layer watcher moves the target to the first member when it runs; the member asked for is chosen after it.
    await nextTick()
    layerId.value = forMember
}

async function loadBindings(): Promise<void> {
    rows.value = await api.toolBindings(
        props.lockLayer && props.lockLayerId ? { layer: props.lockLayer, layerId: props.lockLayerId } : undefined,
    )
    await loadSavedSignIns()
}

/**
 * What each Computer-Use binding has to revoke (#676). Only a Computer-Use binding can hold a sign-in, so
 * an MCP binding is never asked. A binding whose sign-ins cannot be read shows none rather than taking the
 * whole tools page down with it — the bindings themselves are what this page is for.
 */
async function loadSavedSignIns(): Promise<void> {
    const next: Record<string, SavedSignInRow[]> = {}
    await Promise.all(
        rows.value
            .filter(row => row.kind === 'computer-use')
            .map(async row => {
                try {
                    next[row.id] = await api.savedSignIns(row.id)
                } catch {
                    next[row.id] = []
                }
            }),
    )
    savedSignIns.value = next
}

/** The sign-ins to offer revoking on a row, newest first — none at all for an MCP binding. */
function signInsFor(row: ToolBindingRow): SavedSignInRow[] {
    return savedSignIns.value[row.id] ?? []
}

/**
 * Who holds a saved sign-in, in words. When that member is no longer listed, Advanced falls back to the id and
 * Standard says "Unknown member" (#1749), in the text and the revoke button's label alike.
 */
function signInMemberName(memberId: string): string {
    const member = members.value.find(candidate => candidate.id === memberId)
    return personLabel({ id: memberId, name: member ? memberLabel(member) : null }, uiMode.value)
}

/**
 * Takes one saved sign-in back. The next run for that member on that site hits the login wall again and a
 * person signs in once more — which is exactly what revoking is for.
 */
async function revokeSignIn(row: ToolBindingRow, saved: SavedSignInRow): Promise<void> {
    busy.value = true
    error.value = null
    try {
        await api.revokeSavedSignIn(row.id, saved.memberId, saved.site)
        await loadSavedSignIns()
    } catch (caught) {
        // #1815: the copy follows the reason the API named, never the status.
        error.value = revokeRefusalMessage(caught) ?? 'Could not revoke this saved sign-in.'
    } finally {
        busy.value = false
    }
}

function edit(row: ToolBindingRow): void {
    adding.value = false
    scopeOpen.value = false
    editingId.value = row.id
    toolType.value = row.toolType
    displayName.value = row.displayName
    imageUrl.value = row.imageUrl ?? ''
    authMode.value = row.authKind === 'oauth' ? 'oauth' : 'bearer'
    scopes.value = (row.oauthScopes ?? []).join(' ')
    authorizeParams.value = Object.entries(row.authorizeParams ?? {})
        .map(([key, value]) => `${key}=${value}`)
        .join('\n')
    clientId.value = ''
    clientSecret.value = ''
    token.value = ''
    layer.value = row.layer
    layerId.value = row.layerId
    // Edit reopens the saved kind and its own fields (#673, #1406). A credential's secret is never among them.
    kind.value = row.kind ?? 'mcp'
    mcpUrl.value = row.kind === 'web-credential' || row.kind === 'secret' ? '' : row.mcpUrl
    sites.value = (row.sites ?? []).join('\n')
    startUrl.value = row.startUrl ?? ''
    hosts.value = (row.hosts ?? []).join('\n')
    credentialHeader.value = row.header ?? 'authorization'
    credentialScheme.value = row.scheme ?? 'bearer'
    credentialSecret.value = ''
    // A key server shows back where its key goes; one that names nothing sends it as Authorization: Bearer.
    const savedPlacement = savedKeyPlacement(row)
    keyHeader.value = savedPlacement.header
    keyScheme.value = savedPlacement.scheme
    propRows.value = Object.entries(row.props ?? {}).map(([key, value]) => ({ key, value }))
    propsOpen.value = false
    slotsText.value = (row.slots ?? []).join(', ')
    slotsOpen.value = false
    actionsText.value = slotActionsText(row.slotActions)
    actionsOpen.value = false
    fields.reset()
}

function cancelEdit(): void {
    imageUrl.value = ''
    editingId.value = null
    scopeOpen.value = false
    propsOpen.value = false
    slotsText.value = ''
    slotsOpen.value = false
    actionsText.value = ''
    actionsOpen.value = false
    token.value = ''
    credentialSecret.value = ''
    fields.reset()
}

function resetCreateForm(): void {
    adding.value = false
    toolType.value = ''
    displayName.value = ''
    imageUrl.value = ''
    mcpUrl.value = ''
    token.value = ''
    clientId.value = ''
    clientSecret.value = ''
    scopes.value = ''
    authorizeParams.value = ''
    authMode.value = 'bearer'
    kind.value = 'mcp'
    sites.value = ''
    startUrl.value = ''
    hosts.value = ''
    credentialHeader.value = 'authorization'
    credentialScheme.value = 'bearer'
    credentialSecret.value = ''
    keyHeader.value = ''
    keyScheme.value = 'bearer'
    propRows.value = []
    propsOpen.value = false
    slotsText.value = ''
    slotsOpen.value = false
    actionsText.value = ''
    actionsOpen.value = false
    if (!props.lockLayer) {
        layer.value = 'enterprise'
    }
    if (!props.lockLayerId) {
        layerId.value = layer.value === 'enterprise' ? enterpriseId.value : (layerTargets.value[0]?.id ?? '')
    }
    draft.clear()
    fields.reset()
}

async function startOAuth(): Promise<void> {
    // Nothing leaves for the vendor's sign-in while a field breaks its rule: the field says what to fix.
    if (fields.check() !== null) return
    busy.value = true
    error.value = null
    try {
        const target = layer.value === 'enterprise' ? enterpriseId.value : layerId.value
        const started = editingExpress.value
            ? await api.connectExpressTool(toolType.value, { layer: layer.value, layerId: target })
            : await api.startMcpOAuth({
                  toolType: toolType.value,
                  layer: layer.value,
                  layerId: target,
                  mcpUrl: mcpUrl.value,
                  displayName: displayName.value || undefined,
                  clientId: clientId.value || undefined,
                  clientSecret: clientSecret.value || undefined,
                  scopes: scopes.value || undefined,
                  authorizeParams: authorizeParamsFromText(authorizeParams.value),
              })
        window.location.assign(started.authorizationUrl)
    } catch (caught) {
        error.value = oauthStartFailure(caught)
    } finally {
        busy.value = false
    }
}

/** What the form says when an OAuth sign-in could not be started. */
function oauthStartFailure(caught: unknown): string {
    // #1936: a server that refused this app or could not be reached is named, in plain words, never by its code.
    const fromServer = oauthStartRefusalMessage(caught, oauthServerName(displayName.value, mcpUrl.value), {
        express: editingExpress.value,
    })
    if (fromServer) {
        return fromServer
    }
    const code = caught instanceof Error ? caught.message : ''
    if (code === 'client_id_required') {
        return 'This MCP server has no dynamic client registration. Create an OAuth app at the vendor, set the redirect URI below, and paste the client id.'
    }
    if (code === 'oauth_authorize_params_invalid') {
        return AUTHORIZE_PARAMS_INVALID
    }
    if (code === 'oauth_scope_required') {
        return 'Choose at least one permission below, then connect again.'
    }
    if (code === 'oauth_discovery_failed') {
        return 'Could not discover OAuth from that MCP URL (need RFC 9728 / 8414 metadata).'
    }
    return code ? `Could not start OAuth (${code}).` : 'Could not start OAuth.'
}

/**
 * A host credential's half of a save (#1406). The secret goes only when one is typed: an edit that leaves it blank
 * keeps the saved one, and sends no secret at all.
 */
function credentialPayload(): {
    kind: 'web-credential'
    hosts: string[]
    header: string
    scheme: CredentialScheme
    token?: string
} {
    const secret = credentialSecret.value.trim()
    return {
        kind: 'web-credential',
        hosts: hostList.value,
        header: credentialHeader.value.trim(),
        scheme: credentialScheme.value,
        ...(secret ? { token: secret } : {}),
    }
}

/** A stored secret's half of a save: its value, and only when one is typed, as a host credential's goes. */
function secretPayload(): { kind: 'secret'; token?: string } {
    const secret = credentialSecret.value.trim()
    return { kind: 'secret', ...(secret ? { token: secret } : {}) }
}

/**
 * Only the chosen kind's values go over the wire (#673), so a switch drops the other half. An MCP edit sends a token
 * only when one is typed; a new MCP binding always sends what is there.
 */
function kindPayload(editing: boolean) {
    if (isWebCredential.value) {
        return credentialPayload()
    }
    if (isSecret.value) {
        return secretPayload()
    }
    if (isComputerUse.value) {
        return { kind: 'computer-use' as const, sites: siteList.value, startUrl: startUrl.value }
    }
    return {
        kind: 'mcp' as const,
        mcpUrl: mcpUrl.value || undefined,
        token: editing ? token.value || undefined : token.value,
        ...keyPlacementPayload(editing),
    }
}

/** The header and scheme a saved key server names, empty and `bearer` for anything else (or no row at all). */
function savedKeyPlacement(row: ToolBindingRow | undefined): { header: string; scheme: CredentialScheme } {
    const keyServer = row !== undefined && (row.kind ?? 'mcp') === 'mcp' && row.authKind !== 'oauth'
    return { header: keyServer ? (row.header ?? '') : '', scheme: keyServer ? (row.scheme ?? 'bearer') : 'bearer' }
}

/**
 * Where a key binding's key is sent, as a save sends it. A header typed goes as typed, with the scheme beside it; a
 * scheme changed on its own goes alone. Blank ones are not sent, so a new binding sends its key as Authorization:
 * Bearer, and an edit keeps what is saved, except that emptying a header the binding had sends it empty, which puts
 * the key back in Authorization. An OAuth sign-in sends neither: its access token always goes in Authorization.
 */
function keyPlacementPayload(editing: boolean): { header?: string; scheme?: CredentialScheme } {
    if (authMode.value !== 'bearer') return {}
    const saved = editing ? rows.value.find(row => row.id === editingId.value) : undefined
    const { header: savedHeader, scheme: savedScheme } = savedKeyPlacement(saved)
    const typed = keyHeader.value.trim() !== ''
    const placement: { header?: string; scheme?: CredentialScheme } = {}
    if (typed) {
        placement.header = keyHeader.value
    } else if (savedHeader) {
        placement.header = ''
    }
    if (typed || keyScheme.value !== savedScheme) {
        placement.scheme = keyScheme.value
    }
    return placement
}

async function bind(): Promise<void> {
    if (fields.check() !== null) return
    busy.value = true
    error.value = null
    try {
        if (editingId.value) {
            const rescoped = scopeOpen.value
                ? { layer: layer.value, layerId: layer.value === 'enterprise' ? enterpriseId.value : layerId.value }
                : {}
            await api.patchTool(editingId.value, {
                displayName: displayName.value || undefined,
                imageUrl: imageUrl.value,
                ...kindPayload(true),
                ...rescoped,
                props: propsPayload.value,
                slots: slotList.value,
                ...(isMcp.value ? { slotActions: actionMap.value } : {}),
                ...(isMcp.value && authMode.value === 'oauth' && !editingExpress.value
                    ? { authorizeParams: authorizeParamsFromText(authorizeParams.value) }
                    : {}),
            })
            cancelEdit()
        } else {
            const target = layer.value === 'enterprise' ? enterpriseId.value : layerId.value
            await api.bindTool({
                toolType: toolType.value,
                layer: layer.value,
                layerId: target,
                displayName: displayName.value || undefined,
                ...(imageUrl.value ? { imageUrl: imageUrl.value } : {}),
                ...kindPayload(false),
                ...(propCount.value > 0 ? { props: propsPayload.value } : {}),
                ...(slotList.value.length > 0 ? { slots: slotList.value } : {}),
                ...(isMcp.value && actionCount.value > 0 ? { slotActions: actionMap.value } : {}),
            })
            resetCreateForm()
        }
        await loadBindings()
    } catch (caught) {
        // The server's reason, not the status: `request()` throws it as the message (#1815).
        const refusal = bindRefusalMessage(caught)
        if (caught instanceof Error && caught.message === 'oauth_authorize_params_invalid') {
            error.value = 'Use unique name=value lines for additional sign-in parameters.'
        } else if (refusal) {
            error.value = refusal
        } else if (isWebCredential.value) {
            error.value =
                'Could not save this credential. It needs at least one host, a header name and the secret. Type the secret again to add a host or change how it is sent.'
        } else if (isSecret.value) {
            error.value =
                'Could not save this secret. It needs the tool type and the value itself; type the value again to turn another kind of binding into a secret.'
        } else if (isComputerUse.value) {
            error.value =
                'Could not save this Computer-Use binding. It needs at least one site, and a lower layer cannot add a site a wider grant excluded.'
        } else {
            error.value = 'Could not save this MCP server.'
        }
    } finally {
        busy.value = false
    }
}

/** What a bound row points at: the MCP server, where a Computer-Use browser starts, or a credential's hosts. */
function rowTarget(row: ToolBindingRow): string {
    if (row.kind === 'web-credential') {
        return (row.hosts ?? []).join(', ')
    }
    if (row.kind === 'secret') {
        return 'repository commands'
    }
    if (row.kind !== 'computer-use') {
        return row.mcpUrl
    }
    return row.startUrl || 'browser'
}

/**
 * How a bound row authenticates — a Computer-Use binding does not, so it shows its sites instead, and a host
 * credential shows the header it is sent in and how, never the secret.
 */
function rowAuth(row: ToolBindingRow): string {
    if (row.kind === 'web-credential') {
        return `Web credential · ${row.header ?? 'authorization'} · ${row.scheme === 'raw' ? 'value alone' : 'Bearer'}`
    }
    if (row.kind === 'secret') {
        return 'Secret · for the repository commands a task names'
    }
    if (row.kind === 'computer-use') {
        return `Computer-Use · ${(row.sites ?? []).join(', ')}`
    }
    if (row.authKind === 'oauth') {
        return 'OAuth'
    }
    // A key that names where it goes says so; one that names nothing goes as it always has, Authorization: Bearer.
    if (row.header || row.scheme) {
        return `Key · ${row.header ?? 'authorization'} · ${row.scheme === 'raw' ? 'Raw' : 'Bearer'}`
    }
    return 'Bearer token'
}

async function remove(row: ToolBindingRow): Promise<void> {
    busy.value = true
    error.value = null
    try {
        await api.unbindTool(row.id)
        if (editingId.value === row.id) {
            cancelEdit()
        }
        await loadBindings()
    } catch (caught) {
        // #1815: a refused delete says why (an enterprise-wide one is refused with `ceo_bind_only`).
        error.value = bindRefusalMessage(caught) ?? 'Could not remove this tool.'
    } finally {
        busy.value = false
    }
}
</script>

<template>
	<div class="space-y-4">
		<DefineHowBindingWorks>
			Zero Human OS never calls vendor APIs itself. Bind an MCP server here (URL + token, or OAuth discovered from that
			server’s well-known metadata). Company-wide tools are the owner’s, and the members the owner allows. A
			<strong>member</strong> bind is that agent’s own
			credentials. Identity login is not a tool token.
		</DefineHowBindingWorks>
		<div v-if="intro" class="space-y-2">
			<p class="text-muted-foreground text-sm">{{ intro }}</p>
			<ZhDetailsFold label="How this works">
				<p class="text-sm"><HowBindingWorks /></p>
			</ZhDetailsFold>
		</div>
		<p v-else class="text-muted-foreground text-sm"><HowBindingWorks /></p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<!--
			Every surface, control and chip below is the kit's (#1251): the warning is a failed-tone panel, each card the kit
			Card with the status pill, the rows split by the kit separator, and the form a kit panel. The screen states no
			colour, radius, border or fill of its own.
			The error code is Advanced-only (#1749). Its leading space is interpolated: the compiler drops whitespace that
			opens a template, so a typed space would leave "failworkspace_…" in Advanced.
		-->
		<ZhPanel
			v-if="gitMissing"
			as="p"
			tone="failed"
			role="alert"
			class="text-sm"
			data-testid="workspace-git-missing"
		>
			{{ workspaceTasks.length }} {{ workspaceTasks.length === 1 ? 'task works' : 'tasks work' }} in a workspace, and no
			binding here has <code>git.*</code> custom props, so their runs fail<template v-if="advancedMode">
			{{ ' ' }}<code>workspace_repo_no_clone_address</code></template>. Edit your code host’s binding and add the keys its docs page lists
			under Custom props.
		</ZhPanel>
		<div class="grid gap-3 sm:grid-cols-2">
			<Card
				v-for="card in cards"
				:key="card.key"
				data-testid="tool-card"
				:data-tool-type="card.key"
				:data-connected="String(card.connected)"
			>
				<CardContent class="space-y-3">
					<header class="flex items-start justify-between gap-2">
						<ZhSectionHeading as="h3">{{ card.title }}</ZhSectionHeading>
						<ZhStatusPill :tone="card.connected ? 'completed' : 'neutral'" :pulse="false">
							{{ card.connected ? 'Connected' : 'Not connected' }}
						</ZhStatusPill>
					</header>
					<ul class="space-y-2">
						<li
							v-for="(row, index) in card.rows"
							:key="row.id"
							data-testid="tool-card-row"
							:data-tool-type="row.toolType"
							:data-layer="row.layer"
							:data-connected="String(row.connected)"
							class="space-y-2 text-sm"
						>
							<Separator v-if="index > 0" />
							<div class="flex items-start justify-between gap-3">
								<div class="min-w-0">
									<div class="flex items-center gap-2"><img v-if="row.imageUrl" :src="row.imageUrl" alt="" class="size-8 object-contain" /><p class="font-medium">{{ row.displayName }}</p></div>
									<p class="text-muted-foreground truncate">
										<!-- Several prefixes in one card: each row names the one its tasks call it by. -->
										<template v-if="card.toolTypes.length > 1"><code class="font-mono text-xs">{{ row.toolType }}</code> · </template>{{ row.layer }} · {{ rowTarget(row) }}
									</p>
									<p class="text-muted-foreground text-xs">{{ rowAuth(row) }}</p>
									<p v-if="propsOn(row)" class="text-muted-foreground text-xs" data-testid="binding-props-count">
										{{ propsOn(row) }} custom {{ propsOn(row) === 1 ? 'prop' : 'props' }}
									</p>
									<p v-if="row.slots?.length" class="text-muted-foreground text-xs" data-testid="binding-slots">
										Fills: {{ row.slots.join(', ') }}
									</p>
									<p v-if="actionsOn(row)" class="text-muted-foreground text-xs" data-testid="binding-slot-actions">
										Maps {{ actionsOn(row) }} {{ actionsOn(row) === 1 ? 'action' : 'actions' }}
									</p>
									<p v-if="advancedMode && mustGateOn(row)" class="text-muted-foreground text-xs" data-testid="binding-must-gate">
										Must be gated: {{ mustGateOn(row) }}
									</p>
								</div>
								<div class="flex shrink-0 gap-2">
									<Button type="button" size="sm" variant="outline" :disabled="busy" @click="edit(row)">Edit</Button>
									<ZhConfirmButton
										label="Delete"
										confirm-label="Delete binding"
										title="Delete this tool binding?"
										description="Agents bound at this layer lose access to that server on their next run."
										:disabled="busy"
										@confirm="remove(row)"
									/>
								</div>
							</div>
							<p v-if="gitGapFrom(row)" class="text-destructive text-xs" data-testid="binding-git-gap">
								No <code>git.*</code> custom props. The {{ gitGapFrom(row)?.layer }} {{ row.toolType }} binding has
								them, but a run that reaches this binding first reads this one’s: edit it and add them under Custom
								props.
							</p>
							<!--
								#1752: a binding missing the git settings active workspace tasks need says so on its own row the
								moment it is listed (after Bind MCP, an OAuth return or a plain visit), and one button writes them.
								Standard names no prop key and no error code (#1749); Advanced names both, as the page banner does.
							-->
							<ZhPanel
								v-if="gitFills[row.id]"
								as="div"
								tone="failed"
								role="alert"
								class="space-y-2 text-sm"
								data-testid="binding-git-fix"
							>
								<p>
									{{ workspaceTasks.length }} {{ workspaceTasks.length === 1 ? 'task works' : 'tasks work' }} in a
									workspace and will fail on this binding: it is missing the git settings they clone and push with.
									<template v-if="advancedMode">
										It has no <code>git.*</code> custom props, so a run fails with
										<code>workspace_repo_no_clone_address</code>.
									</template>
								</p>
								<Button type="button" size="sm" :disabled="busy" @click="addGitSettings(row, gitFills[row.id] ?? {})">
									Add git settings
								</Button>
							</ZhPanel>
							<!--
								A connection filling a slot whose map lacks actions its directory entry maps: a task naming one parks
								until the map names it. One button adds them, and never changes an action the map already holds.
							-->
							<ZhPanel
								v-if="missingActionsOf(row)"
								as="div"
								tone="action"
								role="status"
								class="space-y-2 text-sm"
								data-testid="binding-actions-fix"
							>
								<p>
									This connection doesn’t say which of its tools to use for
									{{ Object.keys(missingActionsOf(row) ?? {}).length }}
									{{ Object.keys(missingActionsOf(row) ?? {}).length === 1 ? 'action' : 'actions' }} its directory
									entry maps. A task that names one waits until it does.
								</p>
								<p class="text-muted-foreground text-xs" data-testid="binding-actions-missing">
									{{ Object.keys(missingActionsOf(row) ?? {}).join(', ') }}
								</p>
								<Button type="button" size="sm" :disabled="busy" @click="addMissingActions(row, missingActionsOf(row) ?? {})">
									Add missing actions
								</Button>
							</ZhPanel>
							<!-- Issue #676: the saved sign-ins this binding is reusing, and the way to take one back. -->
							<ul v-if="signInsFor(row).length" class="space-y-1" data-testid="saved-sign-ins">
								<li
									v-for="saved in signInsFor(row)"
									:key="`${saved.memberId}:${saved.site}`"
									data-testid="saved-sign-in"
									:data-member-id="saved.memberId"
									:data-site="saved.site"
									class="flex items-center justify-between gap-2"
								>
									<span class="text-muted-foreground min-w-0 truncate text-xs">
										Signed in · {{ signInMemberName(saved.memberId) }} · {{ saved.site }}
									</span>
									<ZhConfirmButton
										label="Revoke sign-in"
										confirm-label="Revoke sign-in"
										title="Revoke this saved sign-in?"
										:aria-label="`Revoke ${signInMemberName(saved.memberId)}’s saved sign-in on ${saved.site}`"
										description="The next run for that member on this site hits the login wall again, and a person signs in once more."
										:disabled="busy"
										@confirm="revokeSignIn(row, saved)"
									/>
								</li>
							</ul>
						</li>
					</ul>
				</CardContent>
			</Card>
		</div>
		<!-- Express tools sit above adding one by hand: the same connections, with nothing to fill in. -->
		<ExpressToolDirectory
			v-if="!showForm"
			:lock-layer="lockLayer"
			:lock-layer-id="lockLayerId"
			:enterprise-id="enterpriseId"
			:teams="expressTeams"
			:members="expressMembers"
			:rows="rows"
		/>
		<!-- On the Tools page, the empty state's one action opens the same form the Add Tool tile opens. -->
		<ZhEmptyState v-if="!cards.length">
			No MCP tools bound at this layer yet. Tools you bind appear here, ready for your tasks to use.
			<template v-if="!lockLayer && !showForm" #action>
				<Button type="button" size="sm" @click="adding = true">Add Tool</Button>
			</template>
		</ZhEmptyState>
		<ZhAddTile v-if="!showForm" label="Add Tool" class="w-36" @click="adding = true" />
		<!--
			#1756: the add form is the shared form shell (a section of the Tools page), one help line on every field, and
			a plain-words preview before anything is bound or a sign-in starts. Editing a bound tool keeps saving at once.
		-->
		<ZhFormLayout
			v-if="showForm"
			:title="saving ? 'Edit Tool' : 'Add Tool'"
			purpose="Connects a tool your tasks can use, at the layer you choose."
			heading="section"
			:framed="saving"
			data-testid="tool-binding-form"
			@submit.prevent="submitForm"
		>
			<template v-if="saving" #status>
				<p class="text-sm">Editing {{ displayName || toolType }}. Leave the token blank to keep the current secret.</p>
			</template>
			<div class="flex justify-end gap-2">
				<Button
					type="button"
					size="sm"
					variant="outline"
					class="shrink-0"
					data-testid="slots-toggle"
					:aria-expanded="String(slotsOpen)"
					:aria-controls="fields.fieldId('slots')"
					@click="toggleSlots"
				>
					Fills slots{{ slotList.length > 0 ? ` (${slotList.length})` : '' }}
				</Button>
				<Button
					v-if="isMcp"
					type="button"
					size="sm"
					variant="outline"
					class="shrink-0"
					data-testid="slot-actions-toggle"
					:aria-expanded="String(actionsOpen)"
					:aria-controls="fields.fieldId('slotActions')"
					@click="toggleActions"
				>
					Action map{{ actionCount > 0 ? ` (${actionCount})` : '' }}
				</Button>
				<Button
					type="button"
					size="sm"
					variant="outline"
					class="shrink-0"
					data-testid="custom-props-toggle"
					:aria-expanded="String(propsOpen)"
					aria-controls="custom-props"
					@click="toggleProps"
				>
					Custom props{{ propCount > 0 ? ` (${propCount})` : '' }}
				</Button>
			</div>
			<!-- #1756: the custom props are a field like any other, with one help line; the editor is its group. -->
			<ZhFormField
				v-if="propsOpen"
				label="Custom props"
				help="Extra key and value settings the tool's docs list; never put a token in one."
				example="org = acme"
				group
			>
			<ZhPanel
				id="custom-props"
				as="fieldset"
				class="space-y-2 text-sm"
				data-testid="custom-props"
			>
				<legend class="sr-only">Custom props</legend>
				<p class="text-muted-foreground">
					Key → value settings this tool reads beyond its server and token. Its docs page lists the keys. They show
					back here, so never put a token in one.
				</p>
				<div v-for="(prop, index) in propRows" :key="index" class="flex gap-2" data-testid="custom-prop">
					<Input
						v-model="prop.key"
						aria-label="Key"
						placeholder="key"
						data-testid="custom-prop-key"
						class="w-2/5 text-xs"
					/>
					<Input
						v-model="prop.value"
						aria-label="Value"
						placeholder="value"
						data-testid="custom-prop-value"
						class="min-w-0 flex-1 text-xs"
					/>
					<Button
						type="button"
						size="sm"
						variant="outline"
						data-testid="custom-prop-remove"
						:aria-label="`Remove ${prop.key || 'this prop'}`"
						@click="propRows.splice(index, 1)"
					>
						Remove
					</Button>
				</div>
				<Button
					type="button"
					size="sm"
					variant="outline"
					data-testid="custom-prop-add"
					@click="propRows.push({ key: '', value: '' })"
				>
					Add prop
				</Button>
			</ZhPanel>
			</ZhFormField>
			<!--
				Tool slots: the keys a task may name its tools by in place of a tool type. A field of its own beside the
				custom props, since `git.repos` among those props is what picks between several bindings of one slot.
			-->
			<!-- Shown while it holds an error too, so a save it refuses never points at a hidden field. -->
			<ZhFormField
				v-if="slotsOpen || fields.message('slots').message"
				:id="fields.fieldId('slots')"
				:error-id="fields.message('slots').id"
				label="Fills slots"
				help="A task that names its tools by a slot (for example codehost) uses this connection for it. When several connections fill one slot, the one whose git.repos custom prop lists the run's repository is used."
				example="codehost, chat"
			>
				<template #default="{ controlId, describedBy }">
					<Input
						v-model="slotsText"
						data-testid="binding-slots-input"
						placeholder="codehost"
						v-bind="fields.bind('slots')"
						:id="controlId"
						:aria-describedby="describedBy"
					/>
				</template>
				<template #error>
					<ZhFieldError v-bind="fields.message('slots')" />
				</template>
			</ZhFormField>
			<!--
				The action map: which of this server's tools each action a slot's task names is. Only a server has tools to
				map, so a browser, a host credential and a secret show none.
			-->
			<ZhFormField
				v-if="isMcp && (actionsOpen || fields.message('slotActions').message)"
				:id="fields.fieldId('slotActions')"
				:error-id="fields.message('slotActions').id"
				label="Action map"
				help="For each action a slot's task names, the tool it is on this server, one action=tool a line. Pin one use of a tool with #field=value. A task naming an action this map leaves out waits until it names it."
				example="pr.merge=pull_request_write#method=merge"
			>
				<template #default="{ controlId, describedBy }">
					<Textarea
						v-model="actionsText"
						data-testid="binding-slot-actions-input"
						rows="6"
						class="font-mono text-xs"
						placeholder="pr.merge=merge_pull_request"
						v-bind="fields.bind('slotActions')"
						:id="controlId"
						:aria-describedby="describedBy"
					/>
				</template>
				<template #error>
					<ZhFieldError v-bind="fields.message('slotActions')" />
				</template>
			</ZhFormField>
			<p v-if="saving && !lockLayer && !scopeOpen" class="text-sm">
				<span class="text-muted-foreground">Applies to {{ layerLabel }}.</span>
				<Button type="button" variant="link" size="sm" class="ml-1 h-auto px-0" @click="scopeOpen = true">
					Change where this applies
				</Button>
			</p>
			<!--
				The choosers are the kit Select: same options, same order, same values as the native ones they replace.
				Each stays inside its label, so the label still names it and a field lookup by label still finds it.
			-->
			<ZhFormField
				v-if="!lockLayer && (!saving || scopeOpen)"
				v-slot="{ controlId, helpId }"
				label="Layer"
				help="Who can use it: the whole company, or one team, member, role or task."
				example="Enterprise"
			>
				<Select :model-value="layer" @update:model-value="value => pickLayer(value as ToolLayer)">
					<SelectTrigger :id="controlId" class="w-full" :aria-describedby="helpId"><SelectValue /></SelectTrigger>
					<SelectContent>
						<SelectItem v-for="option in layerOptions" :key="option.value" :value="option.value">
							{{ option.label }}
						</SelectItem>
					</SelectContent>
				</Select>
			</ZhFormField>
			<ZhFormField
				v-if="!lockLayerId && layer !== 'enterprise' && (!saving || scopeOpen)"
				v-slot="{ controlId, helpId }"
				label="Bind on"
				help="The one team, member, role or task that gets it."
				example="Engineering"
			>
				<Select v-model="layerId">
					<SelectTrigger :id="controlId" class="w-full" :aria-describedby="helpId"><SelectValue /></SelectTrigger>
					<SelectContent>
						<SelectItem v-for="target in layerTargets" :key="target.id" :value="target.id">
							{{ target.label }}
						</SelectItem>
					</SelectContent>
				</Select>
			</ZhFormField>
			<p v-if="saving && scopeOpen" class="text-muted-foreground text-sm">
				Every run at the new layer can use this credential. A team binding is invisible to other teams;
				an enterprise one is visible to all of them, and only the owner, or a member the owner has allowed, can set it.
			</p>
			<ZhFormField label="Kind" help="How it works: an MCP server, a browser it drives, a web credential, or a secret for repo commands." example="MCP" group>
			<fieldset class="block text-sm" data-testid="binding-kind">
				<legend class="sr-only">Kind</legend>
				<label class="mr-3">
					<input type="radio" value="mcp" v-model="kind" data-testid="binding-kind-mcp" />
					MCP
				</label>
				<label class="mr-3">
					<input type="radio" value="computer-use" v-model="kind" data-testid="binding-kind-computer-use" />
					Computer-Use
				</label>
				<label class="mr-3">
					<input type="radio" value="web-credential" v-model="kind" data-testid="binding-kind-web-credential" />
					Web credential
				</label>
				<label>
					<input type="radio" value="secret" v-model="kind" data-testid="binding-kind-secret" />
					Secret
				</label>
			</fieldset>
			</ZhFormField>
			<!--
				#1757's checks on the shared field (CTO decisions 18 and 20 on #1756): each checked control keeps its fixed
				id, is described by its error first and its help line after, and the error sits under the help line.
			-->
			<ZhFormField
				:id="fields.fieldId('toolType')"
				:error-id="fields.message('toolType').id"
				label="Tool type (prefix, e.g. github, linear, slack)"
				help="The short prefix its actions are named with."
				example="github"
			>
				<template #default="{ controlId, describedBy }">
					<Input v-model="toolType" required :disabled="saving" v-bind="fields.bind('toolType')" :id="controlId" :aria-describedby="describedBy" />
				</template>
				<template #error>
					<ZhFieldError v-bind="fields.message('toolType')" />
				</template>
			</ZhFormField>
			<ZhFormField v-slot="{ controlId, helpId }" label="Display name" help="The name your team will see on its card." example="GitHub">
				<Input :id="controlId" v-model="displayName" :aria-describedby="helpId" />
			</ZhFormField>
			<ZhFormField
				v-if="!isWebCredential && !isSecret && (saving || !isMcp || authMode !== 'oauth')"
				label="Image"
				help="A logo for its card; a wrench stands in without one."
				example="the vendor's square logo"
				group
			>
				<ToolImageInput v-model="imageUrl" />
			</ZhFormField>
			<template v-if="isComputerUse">
				<ZhFormField
					:id="fields.fieldId('sites')"
					:error-id="fields.message('sites').id"
					label="Allowed sites (one host per line, no path)"
					help="The only sites its browser may open."
					example="crm.example.com"
					data-testid="binding-sites-label"
				>
					<template #default="{ controlId, describedBy }">
						<Textarea
							v-model="sites"
							data-testid="binding-sites"
							rows="3"
							placeholder="crm.example.com"
							v-bind="fields.bind('sites')"
							:id="controlId"
							:aria-describedby="describedBy"
						/>
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('sites')" />
					</template>
				</ZhFormField>
				<ZhFormField
					v-slot="{ controlId, helpId }"
					label="Start URL"
					help="The page its browser opens first."
					:example="START_URL_EXAMPLE"
					data-testid="binding-start-url-label"
				>
					<Input :id="controlId" v-model="startUrl" data-testid="binding-start-url" :placeholder="URL_PLACEHOLDER" :aria-describedby="helpId" />
				</ZhFormField>
				<p class="text-muted-foreground text-sm">
					A run only gets a browser when its task lists <code class="font-mono text-xs">{{ toolType || 'prefix' }}.navigate</code>
					(or another action). A lower layer may drop a site this grant allows; adding one it does not needs
					whoever owns the wider grant.
				</p>
			</template>
			<!--
				#1406: a credential the web tools add to requests to these hosts and no other, over https only. The
				model never reads it, and it is not shown back here once saved.
			-->
			<template v-if="isWebCredential">
				<ZhFormField
					:id="fields.fieldId('hosts')"
					:error-id="fields.message('hosts').id"
					label="Hosts (one per line, exact: no scheme, path or wildcard)"
					help="The only hosts the secret is ever sent to."
					example="api.example.com"
				>
					<template #default="{ controlId, describedBy }">
						<Textarea
							v-model="hosts"
							data-testid="binding-hosts"
							rows="3"
							placeholder="api.example.com"
							v-bind="fields.bind('hosts')"
							:id="controlId"
							:aria-describedby="describedBy"
						/>
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('hosts')" />
					</template>
				</ZhFormField>
				<ZhFormField
					:id="fields.fieldId('credentialHeader')"
					:error-id="fields.message('credentialHeader').id"
					label="Header it is sent in"
					help="The request header that carries the secret."
					example="authorization"
				>
					<template #default="{ controlId, describedBy }">
						<Input
							v-model="credentialHeader"
							data-testid="binding-credential-header"
							placeholder="authorization"
							autocomplete="off"
							v-bind="fields.bind('credentialHeader')"
							:id="controlId"
							:aria-describedby="describedBy"
						/>
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('credentialHeader')" />
					</template>
				</ZhFormField>
				<ZhFormField v-slot="{ controlId, helpId }" label="How it is sent" help="Whether the secret goes after the word Bearer or on its own." example="Bearer">
					<Select v-model="credentialScheme" data-testid="binding-credential-scheme">
						<SelectTrigger :id="controlId" class="w-full" :aria-describedby="helpId"><SelectValue /></SelectTrigger>
						<SelectContent>
							<SelectItem value="bearer">Bearer &lt;secret&gt;</SelectItem>
							<SelectItem value="raw">The secret alone</SelectItem>
						</SelectContent>
					</Select>
				</ZhFormField>
			</template>
			<template v-if="isWebCredential || isSecret">
				<ZhFormField
					:id="fields.fieldId('credentialSecret')"
					:error-id="fields.message('credentialSecret').id"
					label="Secret"
					help="The key or token itself; it is never shown again."
					example="a key from the vendor's settings page"
				>
					<template #default="{ controlId, describedBy }">
						<Input
							v-model="credentialSecret"
							data-testid="binding-credential-secret"
							type="password"
							autocomplete="off"
							:placeholder="saving ? 'Leave blank to keep current' : ''"
							v-bind="fields.bind('credentialSecret')"
							:id="controlId"
							:aria-describedby="describedBy"
						/>
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('credentialSecret')" />
					</template>
				</ZhFormField>
				<p v-if="saving && isWebCredential" class="text-muted-foreground text-sm" data-testid="binding-credential-retype">
					Adding a host, or changing the header or how it is sent, needs the secret typed again. Removing a
					host does not.
				</p>
				<p v-if="isWebCredential" class="text-muted-foreground text-sm">
					Tasks under this layer send it with <code class="font-mono text-xs">web.*</code> calls to these hosts
					only, and never on a redirect to another host. The model never reads it, and a reply that repeats it
					has it masked.
				</p>
				<p v-else class="text-muted-foreground text-sm" data-testid="binding-secret-note">
					Tasks under this layer get it in the environment of the repository commands their version names in
					<code class="font-mono text-xs">workspace.credentials</code>, and nowhere else. The model never reads
					it, and a command that prints it has it masked.
				</p>
			</template>
			<ZhFormField
				v-if="isMcp"
				:id="fields.fieldId('mcpUrl')"
				:error-id="fields.message('mcpUrl').id"
				label="MCP server URL"
				help="Where the tool's MCP server answers."
				:example="MCP_URL_EXAMPLE"
			>
				<template #default="{ controlId, describedBy }">
					<Input v-model="mcpUrl" :placeholder="URL_PLACEHOLDER" v-bind="fields.bind('mcpUrl')" :id="controlId" :aria-describedby="describedBy" />
				</template>
				<template #error>
					<ZhFieldError v-bind="fields.message('mcpUrl')" />
				</template>
			</ZhFormField>
			<ZhFormField v-if="isMcp" v-slot="{ controlId, helpId }" label="Auth" help="How it signs in: a key you paste, or the vendor's own sign-in page." example="Bearer token">
				<Select v-model="authMode">
					<SelectTrigger :id="controlId" class="w-full" :aria-describedby="helpId"><SelectValue /></SelectTrigger>
					<SelectContent>
						<SelectItem value="bearer">Bearer token / PAT</SelectItem>
						<SelectItem value="oauth">OAuth (discovered from the MCP server)</SelectItem>
					</SelectContent>
				</Select>
			</ZhFormField>
			<ZhFormField
				v-if="isMcp && authMode === 'bearer'"
				:id="fields.fieldId('token')"
				:error-id="fields.message('token').id"
				label="API key / PAT / bearer token"
				help="The key the server accepts; it is never shown again."
				example="a personal access token"
			>
				<template #default="{ controlId, describedBy }">
					<Input
						v-model="token"
						type="password"
						autocomplete="off"
						:required="!saving"
						:placeholder="saving ? 'Leave blank to keep current' : ''"
						v-bind="fields.bind('token')"
						:id="controlId"
						:aria-describedby="describedBy"
					/>
				</template>
				<template #error>
					<ZhFieldError v-bind="fields.message('token')" />
				</template>
			</ZhFormField>
			<!-- Where the key is sent: whatever header and scheme the server asks for. OAuth always uses Authorization. -->
			<template v-if="isMcp && authMode === 'bearer'">
				<ZhFormField
					v-slot="{ controlId, helpId }"
					label="Header"
					help="The request header the key is sent in; leave it empty to send it in authorization."
					example="x-api-key"
				>
					<Input
						:id="controlId"
						v-model="keyHeader"
						data-testid="binding-key-header"
						placeholder="authorization"
						autocomplete="off"
						:aria-describedby="helpId"
					/>
				</ZhFormField>
				<ZhFormField
					v-slot="{ controlId, helpId }"
					label="Scheme"
					help="Whether the key goes after the word Bearer, or on its own (Raw)."
					example="Bearer"
				>
					<Select v-model="keyScheme" data-testid="binding-key-scheme">
						<SelectTrigger :id="controlId" class="w-full" :aria-describedby="helpId"><SelectValue /></SelectTrigger>
						<SelectContent>
							<SelectItem value="bearer">Bearer</SelectItem>
							<SelectItem value="raw">Raw</SelectItem>
						</SelectContent>
					</Select>
				</ZhFormField>
			</template>
			<template v-if="isMcp && authMode === 'oauth'">
				<p v-if="redirectUri" class="text-sm">
					Register this redirect URI on the vendor OAuth app:
					<code class="font-mono text-xs break-all">{{ redirectUri }}</code>
				</p>
				<!-- #1936: the refusal copy points here for the redirect URI, so say why it is missing rather than nothing. -->
				<p v-else-if="!editingExpress" class="text-muted-foreground text-sm" data-testid="oauth-redirect-missing">
					The redirect URI to register on the vendor OAuth app could not be loaded. Reload the page to see it.
				</p>
				<template v-if="!editingExpress">
				<ZhFormField v-slot="{ controlId, helpId }" label="OAuth client id" help="The id of the app you made at the vendor; blank if it registers itself." example="from the vendor's app settings">
					<Input :id="controlId" v-model="clientId" :aria-describedby="helpId" />
				</ZhFormField>
				<ZhFormField v-slot="{ controlId, helpId }" label="OAuth client secret (confidential apps)" help="That app's secret, only if the vendor gave one." example="from the vendor's app settings">
					<Input :id="controlId" v-model="clientSecret" type="password" autocomplete="off" :aria-describedby="helpId" />
				</ZhFormField>
				<!-- The parameters keep their own label and id (`oauth-authorize-params`), so this field heads them as a group. -->
				<ZhFormField
					:error-id="fields.message('authorizeParams').id"
					label="Sign-in parameters"
					help="Extra name=value settings the vendor's sign-in asks for, if any."
					example="prompt=consent"
					group
				>
					<template #default="{ describedBy }">
						<OAuthAuthorizeParams v-model="authorizeParams" v-bind="fields.bind('authorizeParams')" :aria-describedby="describedBy" />
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('authorizeParams')" />
					</template>
				</ZhFormField>
				<ZhFormField label="Permissions" help="What the tool may do once you sign in." example="read issues" group>
					<McpPermissionsPicker v-model="scopes" :mcp-url="mcpUrl" />
				</ZhFormField>
				</template>
				<p v-else class="text-muted-foreground text-sm">Sign-in settings are managed by this Express tool. Reconnect to sign in again.</p>
			</template>
			<template #cancel>
				<Button
					v-if="saving || adding"
					type="button"
					size="sm"
					variant="outline"
					:disabled="busy"
					@click="saving ? cancelEdit() : resetCreateForm()"
				>
					Cancel
				</Button>
				<Button v-if="saving && isMcp && authMode === 'oauth'" type="button" size="sm" variant="outline" :disabled="busy" @click="bind">Save settings</Button>
			</template>
			<template #primary>
				<Button
					v-if="isComputerUse"
					type="submit"
					size="sm"
					:disabled="busy || !toolType || !siteList.length"
				>
					{{ primaryLabel }}
				</Button>
				<Button
					v-else-if="isWebCredential"
					type="submit"
					size="sm"
					:disabled="busy || !toolType || !hostList.length || !credentialHeader.trim() || (!saving && !credentialSecret)"
				>
					{{ primaryLabel }}
				</Button>
				<Button v-else-if="isSecret" type="submit" size="sm" :disabled="busy || !toolType || (!saving && !credentialSecret)">
					{{ primaryLabel }}
				</Button>
				<Button
					v-else-if="authMode === 'bearer'"
					type="submit"
					size="sm"
					:disabled="busy || !toolType || (!saving && !token)"
				>
					{{ primaryLabel }}
				</Button>
				<Button
					v-else
					type="button"
					size="sm"
					:disabled="busy || !toolType || !mcpUrl"
					@click="saving ? startOAuth() : openPreview()"
				>
					{{ primaryLabel }}
				</Button>
			</template>
			<template #preview>
				<ZhSavePreview v-if="preview" :lines="preview" :confirm-label="primaryLabel" :pending="busy" @confirm="confirmPreview" @edit="preview = null" />
			</template>
		</ZhFormLayout>
	</div>
</template>
